<template>
    <Modal id="held-carts-modal"
           data-component="HeldCartsModal"
           title="ORDERS_ON_HOLD.RESTORE_MODAL_TITLE"
           icon="fa-cloud-arrow-down"
           :cancel="cancel">
        <PageLoadingSpinner v-if="loading"></PageLoadingSpinner>
        <template v-else>
            <div class="mb-1 btn-group btn-group-justified">
                <label class="btn btn-default"
                       :class="{'active': !showAllUsers}"
                       @click.prevent="setShowAllUsers(false)">
                    {{ l('ORDERS_ON_HOLD.ORDERS_FOR_CURRENT_OPERATOR_BUTTON') }}
                </label>
                <label class="btn btn-default"
                       :class="{'active': showAllUsers}"
                       @click.prevent="setShowAllUsers(true)">
                    {{ l('ORDERS_ON_HOLD.ORDERS_FOR_ALL_OPERATORS_BUTTON') }}
                </label>
            </div>

            <CartOverview v-for="stashedCart in sortedFilteredStashedCarts"
                          :key="stashedCart.thirdPartyRef"
                          :stashed-cart="stashedCart"
                          :show-delete="true"
                          :show-restore="true"
                          @delete-clicked="deleteCartWithConfirmation(stashedCart)"
                          @restore-clicked="restoreCart(stashedCart)"
                          @preview-clicked="previewCart(stashedCart)"></CartOverview>
        </template>
    </Modal>
</template>

<script>
import CartOverview from '@/epos/vue/modals/common/CartOverview';
import Modal from '@/vue/components/Modals/Modal';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import { container } from '@/container';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { timeoutPromise } from '@/functions/functions';

export default {
    name: 'HeldCartsModal',

    components: {
        Modal,
        PageLoadingSpinner,
        CartOverview,
    },

    mixins: [
        ModalPropsMixin,
    ],

    data() {
        return {
            loading: true,
            showAllUsers: false,

            /**
             * @type {StashedCart[]}
             */
            stashedCarts: [],
        };
    },

    computed: {
        /**
         * Filters to show only orders created by the current operator by default.
         * Sorts and displays oldest stashes first.
         *
         * @return {StashedCart[]}
         */
        sortedFilteredStashedCarts() {
            let carts = this.stashedCarts;

            if (!this.showAllUsers) {
                carts = this.stashedCarts.filter((cart) => cart.username === container.userService.username);
            }

            carts.sort((a, b) => b.stashedAt - a.stashedAt);

            return carts;
        },
    },

    created() {
        container.cartStasher.loadStashedCarts()
            .then((stashedCarts) => {
                this.loading = false;
                this.stashedCarts = stashedCarts;
            });
    },

    methods: {
        setShowAllUsers(value) {
            this.showAllUsers = value;
        },

        /**
         * @param {StashedCart} stashedCart
         * @param {?Number} [childModalID] Identifier of a child modal calling back so that we can
         */
        deleteCartWithConfirmation(stashedCart, childModalID) {
            container.dialogService
                .confirm('ORDERS_ON_HOLD.CONFIRM_DELETE')
                .then((confirmed) => {
                    if (confirmed) {
                        return this.deleteCart(stashedCart, childModalID);
                    }
                    // The user cancelled
                    return null;
                });
        },

        /**
         * @param {StashedCart} stashedCart
         * @param {?Number} childModalID Identifier of a child modal calling back so that we can close if necessary.
         */
        async deleteCart(stashedCart, childModalID) {
            stashedCart.isDeleting = true;

            return Promise
                .all([
                    container.userSessionManager.deleteStashedCartByThirdPartyRef(stashedCart.thirdPartyRef).catch(() => {}),
                    container.cartStasher.deleteRemoteStashedCart(stashedCart).catch(() => {}),
                ])
                .then(() => {
                    const index = this.stashedCarts.indexOf(stashedCart);
                    if (index !== -1) {
                        if (childModalID) {
                            container.vueModalFactory.close(childModalID);
                        }
                        this.stashedCarts.splice(index, 1);
                    }
                })
                .finally(() => {
                    stashedCart.isDeleting = false;
                });
        },

        /**
         * @param {StashedCart} stashedCart
         * @param {?Number} [childModalID] Identifier of a child modal calling back so that we can close if necessary.
         */
        restoreCart(stashedCart, childModalID) {
            stashedCart.isRestoring = true;

            container.cartService.populateFromStash(stashedCart)
                .then(() => {
                    this.deleteCart(stashedCart);

                    // Having a tiny delay here makes it feel less jarring because if it all happens as soon as you
                    // click the button it's not clear where things went.
                    return timeoutPromise(200);
                }).then(() => {
                    if (childModalID) {
                        container.vueModalFactory.close(childModalID);
                    }
                    this.resolve({
                        restored: true,
                    });
                })
                .finally(() => {
                    stashedCart.isRestoring = false;
                });
        },

        /**
         * @param {StashedCart} stashedCart
         */
        previewCart(stashedCart) {
            container.vueModalFactory.openAndIgnoreResult(
                MODAL_NAMES__EPOS.ORDER_PREVIEW,
                {
                    stashedCart,
                    parentModal: this,
                    title: 'ORDER_PREVIEW_MODAL.STASHED_CART_TITLE',
                }
            );
        },
    },
};
</script>
